import Axios from 'axios';
import { environment } from "../../environment";
import { errorCatcher, useSnackbar } from "../../hooks";
import { useLogin } from "../login";
import { useAtom } from "jotai";
import { recentInsertsAtom } from "./types";

type CheckForRecentInsertsType = () => Promise<boolean | { error: string; showError: boolean; errorType?: string; storeName?: string; storeDomain?: string } | null>;

type AppRequestsKeyFunctions = {
    check: CheckForRecentInsertsType;
    setRecent: (payload: boolean) => void;
}

type UseAppRequests = [
    boolean | { error: string; showError: boolean; errorType?: string; storeName?: string; storeDomain?: string } | null,
    AppRequestsKeyFunctions
];

export const useAppRequests = (): UseAppRequests => {
    const [recentInserts, setRecentInserts] = useAtom(recentInsertsAtom);
    const [, setSnackbar] = useSnackbar();
    const [,authToken, loginFunctions] = useLogin();

    const checkForRecentInserts = async (): Promise<boolean | { error: string; showError: boolean; errorType?: string; storeName?: string; storeDomain?: string } | null> => {
        try {
            const results = await Axios.get(`${environment.appRequestsUrl}/check`, {
                headers: {
                    'X-Wallmates-Auth': authToken
                }
            });
            
            // Check if the response is an error response with showError: true
            if (results.data && typeof results.data === 'object' && 'message' in results.data && 'showError' in results.data && results.data.showError === true) {
                // Return error object with trace data for field-specific errors
                const trace = results.data.trace || {};
                return {
                    error: results.data.message,
                    showError: true,
                    errorType: trace.errorType,
                    storeName: trace.storeName,
                    storeDomain: trace.storeDomain,
                };
            }
            // Otherwise, it's a boolean (recent inserts status)
            setRecentInserts(results.data);
            return results.data;
        } catch (err: any) {
            // Handle 400 Bad Request as valid data (contains error response with showError: true)
            if (err.response && err.response.status === 400 && err.response.data) {
                const errorData = err.response.data;
                // Check if it's a user-facing error response
                if (errorData && typeof errorData === 'object' && 'message' in errorData && 'showError' in errorData && errorData.showError === true) {
                    const trace = errorData.trace || {};
                    const recentInserts = {
                        error: errorData.message,
                        showError: true,
                        errorType: trace.errorType,
                        storeName: trace.storeName,
                        storeDomain: trace.storeDomain,
                    }
                    setRecentInserts(recentInserts);
                    return recentInserts;
                }
            }
            
            // For other errors, use errorCatcher
            errorCatcher(err, loginFunctions.logout);
            setSnackbar({
                show: true,
                snackbarLevel: 'error',
                text: 'There was an error while checking for recent inserts. Please try again later.'
            });
            return null;
        }
    }

    const keyFunctions: AppRequestsKeyFunctions = {
        check: checkForRecentInserts,
        setRecent: setRecentInserts
    }

    return [
        recentInserts,
        keyFunctions
    ]
}